import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AudioPlayer from '../../../components/AudioPlayer';
import WaveformVisualizer from '../../../components/WaveformVisualizer';
import { analyzePronunciation, generatePracticeExercises } from '../../../services/pronunciationAnalysisService';

const PronunciationQuestion = ({ line, characterName, playName, audioUrl, onAnswer, isAnswered }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [showPracticeExercises, setShowPracticeExercises] = useState(false);
  const [practiceExercises, setPracticeExercises] = useState(null);
  const [audioWaveformUrl, setAudioWaveformUrl] = useState(null);
  const [wordTimestamps, setWordTimestamps] = useState([]);
  const [attemptHistory, setAttemptHistory] = useState([]);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef?.current?.push(event?.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await handleAnalyzePronunciation(audioBlob);
        stream?.getTracks()?.forEach(track => track?.stop());
      };

      mediaRecorderRef?.current?.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) {
            handleStopRecording();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      setAnalysisError('Unable to access microphone. Please check your permissions.');
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef?.current && isRecording) {
      mediaRecorderRef?.current?.stop();
      setIsRecording(false);
      setHasRecorded(true);
      if (timerIntervalRef?.current) {
        clearInterval(timerIntervalRef?.current);
      }
    }
  };

  const handleAnalyzePronunciation = async (audioBlob) => {
    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const result = await analyzePronunciation(line, characterName, playName, audioBlob);
      setFeedback(result?.feedback);
      setAudioWaveformUrl(result?.audioUrl);
      setWordTimestamps(result?.wordTimestamps || []);
      
      // Track attempt history for improvement tracking
      setAttemptHistory(prev => [...prev, {
        clarityScore: result?.feedback?.clarityScore || 0,
        issueCount: result?.feedback?.phonemeAnalysis?.length || 0,
        timestamp: new Date()?.toISOString()
      }]);
    } catch (error) {
      setAnalysisError(error?.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGetPracticeExercises = async () => {
    try {
      const exercises = await generatePracticeExercises(feedback?.areasForImprovement || [], line);
      setPracticeExercises(exercises);
      setShowPracticeExercises(true);
    } catch (error) {
      setAnalysisError('Unable to generate practice exercises. Please try again.');
    }
  };

  const handleSubmit = async (isCorrect) => {
    if (isSubmittingAnswer) return;
    setIsSubmittingAnswer(true);
    try {
      await onAnswer(isCorrect);
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getQualityBadge = (quality) => {
    const badges = {
      'excellent': { color: 'bg-success/20 text-success border-success/30', label: 'Excellent' },
      'good': { color: 'bg-primary/20 text-primary border-primary/30', label: 'Good' },
      'fair': { color: 'bg-warning/20 text-warning border-warning/30', label: 'Fair' },
      'needs-improvement': { color: 'bg-destructive/20 text-destructive border-destructive/30', label: 'Needs Improvement' }
    };
    return badges?.[quality] || badges?.['fair'];
  };

  return (
    <div className="bg-card border border-border rounded-xl p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Mic" size={24} className="text-accent" />
          <h3 className="text-xl font-semibold text-foreground">AI-Powered Pronunciation Challenge</h3>
        </div>
        <p className="text-muted-foreground mb-6">
          Record yourself reading this line aloud. Our AI will analyze your pronunciation, expression, and provide personalized coaching feedback.
        </p>
      </div>
      <div className="bg-muted/30 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Quote" size={20} className="text-primary" />
          <span className="font-extrabold text-primary text-sm uppercase tracking-widest">
            {characterName}
          </span>
          <span className="text-xs italic text-muted-foreground/70 tracking-wide">— {playName}</span>
        </div>
        <p className="text-foreground text-base leading-8 tracking-normal font-serif italic">
          "{line}"
        </p>
      </div>
      <div className="space-y-6">
        <div className="bg-background/50 rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Headphones" size={20} className="text-secondary" />
            <h4 className="font-semibold text-foreground">Reference Audio</h4>
          </div>
          <AudioPlayer
            audioUrl={audioUrl}
            label="Play Reference Audio"
            size="lg"
            variant="default"
            showDuration={true}
            onPlayStart={() => {}}
            onPlayEnd={() => {}}
          />
          <p className="text-sm text-muted-foreground mt-3">
            Listen carefully to the pronunciation, tone, and rhythm. You can replay as many times as needed.
          </p>
        </div>

        <div className="bg-background/50 rounded-xl p-6 border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="Mic" size={20} className="text-accent" />
            <h4 className="font-semibold text-foreground">Your Recording</h4>
          </div>

          <div className="flex flex-col items-center gap-4">
            {!hasRecorded ? (
              <>
                {!isRecording ? (
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Mic"
                    iconPosition="left"
                    onClick={handleStartRecording}
                    disabled={isAnswered}
                  >
                    Start Recording
                  </Button>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-lg font-medium text-foreground">Recording... {recordingTime}s / 30s</span>
                    </div>
                    <Button
                      variant="destructive"
                      size="lg"
                      iconName="Square"
                      iconPosition="left"
                      onClick={handleStopRecording}
                    >
                      Stop Recording
                    </Button>
                  </>
                )}
                <p className="text-sm text-muted-foreground text-center">
                  Record yourself reading the line. You have up to 30 seconds. AI analysis will begin automatically.
                </p>
              </>
            ) : isAnalyzing ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="font-medium text-foreground">Analyzing pronunciation with AI...</span>
                <span className="text-sm text-muted-foreground">This may take a few moments</span>
              </div>
            ) : feedback ? (
              <div className="w-full space-y-6">
                {/* Enhanced Waveform Visualization with Clarity Score and Progress Tracking */}
                {audioWaveformUrl && (
                  <WaveformVisualizer 
                    audioUrl={audioWaveformUrl}
                    height={120}
                    phonemeAnalysis={feedback?.phonemeAnalysis?.map(p => ({
                      ...p,
                      timestamp: p?.timestamp || 0,
                      duration: p?.duration || 0.5
                    })) || []}
                    clarityScore={feedback?.clarityScore || 0}
                    previousAttempts={attemptHistory?.slice(0, -1)}
                  />
                )}

                {/* Scores Section */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-sm text-muted-foreground mb-1">Overall Score</div>
                    <div className={`text-3xl font-bold ${getScoreColor(feedback?.overallScore)}`}>
                      {feedback?.overallScore}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">out of 100</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-sm text-muted-foreground mb-1">Accuracy</div>
                    <div className={`text-3xl font-bold ${getScoreColor(feedback?.accuracyScore)}`}>
                      {feedback?.accuracyScore}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">pronunciation</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-sm text-muted-foreground mb-1">Expression</div>
                    <div className={`text-3xl font-bold ${getScoreColor(feedback?.expressionScore)}`}>
                      {feedback?.expressionScore}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">delivery</div>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-sm text-muted-foreground mb-1">Clarity</div>
                    <div className={`text-3xl font-bold ${getScoreColor(feedback?.clarityScore)}`}>
                      {feedback?.clarityScore}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">enunciation</div>
                  </div>
                </div>

                {/* Quality Badge */}
                <div className="flex justify-center">
                  <span className={`px-4 py-2 rounded-full border font-medium ${getQualityBadge(feedback?.pronunciationQuality)?.color}`}>
                    {getQualityBadge(feedback?.pronunciationQuality)?.label}
                  </span>
                </div>

                {/* Phoneme-Level Analysis */}
                {feedback?.phonemeAnalysis?.length > 0 && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <Icon name="Search" size={20} className="text-accent" />
                      <h5 className="font-semibold text-foreground">Phoneme-Level Analysis</h5>
                      <span className="text-xs px-2 py-1 rounded bg-accent/20 text-accent ml-auto">
                        Detailed Pronunciation Breakdown
                      </span>
                    </div>
                    <div className="space-y-4">
                      {feedback?.phonemeAnalysis?.map((phoneme, index) => (
                        <div 
                          key={index}
                          className={`border rounded-lg p-5 ${
                            phoneme?.severity === 'critical' ? 'border-destructive/50 bg-destructive/5' :
                            phoneme?.severity === 'moderate'? 'border-warning/50 bg-warning/5' : 'border-border bg-muted/20'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-lg text-foreground">{phoneme?.word}</span>
                                <span className={`text-xs px-2 py-1 rounded font-medium ${
                                  phoneme?.severity === 'critical' ? 'bg-destructive/20 text-destructive' :
                                  phoneme?.severity === 'moderate'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                                }`}>
                                  {phoneme?.severity}
                                </span>
                              </div>
                              {phoneme?.expectedPronunciation && (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-muted-foreground">Expected:</span>
                                  <span className="font-mono text-foreground">{phoneme?.expectedPronunciation}</span>
                                </div>
                              )}
                              {phoneme?.studentPronunciation && (
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-muted-foreground">You said:</span>
                                  <span className="font-mono text-foreground">{phoneme?.studentPronunciation}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-foreground mb-1">Issue:</div>
                                <p className="text-sm text-muted-foreground">{phoneme?.issue}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="text-sm font-medium text-foreground mb-1">How to Fix:</div>
                                <p className="text-sm text-foreground">{phoneme?.correction}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rhythm and Meter Analysis */}
                {feedback?.rhythmAndMeter && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="Music" size={20} className="text-purple-500" />
                      <h5 className="font-semibold text-purple-500">Rhythm & Meter Analysis</h5>
                      <span className={`ml-auto text-2xl font-bold ${getScoreColor(feedback?.rhythmAndMeter?.score)}`}>
                        {feedback?.rhythmAndMeter?.score}/100
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{feedback?.rhythmAndMeter?.feedback}</p>
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-foreground">Quick Tips:</div>
                      <ul className="space-y-1">
                        {feedback?.rhythmAndMeter?.tips?.map((tip, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Icon name="Zap" size={14} className="text-purple-500 flex-shrink-0 mt-0.5" />
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Strengths */}
                <div className="bg-success/10 border border-success/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="ThumbsUp" size={20} className="text-success" />
                    <h5 className="font-semibold text-success">What You Did Well</h5>
                  </div>
                  <ul className="space-y-2">
                    {feedback?.strengths?.map((strength, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                        <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Areas for Improvement */}
                {feedback?.areasForImprovement?.length > 0 && (
                  <div className="bg-warning/10 border border-warning/30 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="Target" size={20} className="text-warning" />
                      <h5 className="font-semibold text-warning">Areas to Focus On</h5>
                    </div>
                    <div className="space-y-4">
                      {feedback?.areasForImprovement?.map((item, index) => (
                        <div key={index} className="border-l-2 border-warning pl-4">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-foreground">{item?.area}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              item?.priority === 'high' ? 'bg-destructive/20 text-destructive' :
                              item?.priority === 'medium'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                            }`}>
                              {item?.priority}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{item?.suggestion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Coaching Tips */}
                <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Lightbulb" size={20} className="text-primary" />
                    <h5 className="font-semibold text-primary">Coaching Tips</h5>
                  </div>
                  <ul className="space-y-2">
                    {feedback?.coachingTips?.map((tip, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                        <Icon name="Sparkles" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Encouragement */}
                <div className="bg-accent/10 border border-accent/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Heart" size={20} className="text-accent" />
                    <h5 className="font-semibold text-accent">Keep Going!</h5>
                  </div>
                  <p className="text-foreground">{feedback?.encouragement}</p>
                </div>

                {/* Next Steps */}
                <div className="bg-secondary/10 border border-secondary/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="TrendingUp" size={20} className="text-secondary" />
                    <h5 className="font-semibold text-secondary">Next Steps</h5>
                  </div>
                  <ol className="space-y-2">
                    {feedback?.nextSteps?.map((step, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-foreground">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary/20 text-secondary flex items-center justify-center font-semibold text-xs">
                          {index + 1}
                        </span>
                        <span className="mt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Practice Exercises Button */}
                {!showPracticeExercises && feedback?.areasForImprovement?.length > 0 && (
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="BookOpen"
                      iconPosition="left"
                      onClick={handleGetPracticeExercises}
                    >
                      Get Personalized Practice Exercises
                    </Button>
                  </div>
                )}

                {/* Practice Exercises */}
                {showPracticeExercises && practiceExercises && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <Icon name="BookOpen" size={20} className="text-primary" />
                      <h5 className="font-semibold text-foreground">Personalized Practice Exercises</h5>
                    </div>
                    <div className="space-y-6">
                      {practiceExercises?.exercises?.map((exercise, index) => (
                        <div key={index} className="border border-border rounded-lg p-5">
                          <div className="flex items-start justify-between mb-3">
                            <h6 className="font-semibold text-foreground">{exercise?.title}</h6>
                            <span className="text-xs px-2 py-1 rounded bg-primary/20 text-primary">
                              {exercise?.focusArea}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-4">{exercise?.description}</p>
                          <div className="space-y-2">
                            <div className="text-sm font-medium text-foreground mb-2">Steps:</div>
                            <ol className="space-y-2">
                              {exercise?.steps?.map((step, stepIndex) => (
                                <li key={stepIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-muted text-foreground flex items-center justify-center text-xs font-medium">
                                    {stepIndex + 1}
                                  </span>
                                  <span className="mt-0.5">{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {!isAnswered && (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => {
                        setHasRecorded(false);
                        setRecordingTime(0);
                        setFeedback(null);
                        setShowPracticeExercises(false);
                        setPracticeExercises(null);
                        setAudioWaveformUrl(null);
                        setWordTimestamps([]);
                        // Keep attempt history for tracking progress
                      }}
                      fullWidth
                      disabled={isSubmittingAnswer}
                    >
                      Try Again
                    </Button>
                    <Button
                      variant="default"
                      size="md"
                      onClick={() => handleSubmit(feedback?.overallScore >= 70)}
                      fullWidth
                      disabled={isSubmittingAnswer}
                      iconName={isSubmittingAnswer ? undefined : undefined}
                    >
                      {isSubmittingAnswer ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                          Saving XP…
                        </span>
                      ) : 'Continue'}
                    </Button>
                  </div>
                )}
              </div>
            ) : analysisError ? (
              <div className="w-full space-y-4">
                <div className="flex items-center gap-3 text-destructive">
                  <Icon name="AlertCircle" size={24} />
                  <span className="font-medium">Analysis Error</span>
                </div>
                <p className="text-sm text-muted-foreground">{analysisError}</p>
                <Button
                  variant="outline"
                  size="md"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={() => {
                    setHasRecorded(false);
                    setRecordingTime(0);
                    setAnalysisError(null);
                  }}
                  fullWidth
                >
                  Try Recording Again
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {isAnswered && !feedback && (
        <div className="mt-6 p-4 bg-success/10 border border-success/30 rounded-lg flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-semibold text-success mb-1">Pro Tips</h5>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Pay attention to the rhythm and meter of the verse</li>
              <li>• Emphasize key words that carry emotional weight</li>
              <li>• Practice breath control for longer lines</li>
              <li>• Listen for subtle pronunciation differences in archaic words</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PronunciationQuestion;